"use client";

import { Check, Music, Users as Users2, Heart } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhatWeDo() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const channels = [{
    number: "01",
    title: "Friday Socials",
    description: "High-energy weekly gatherings featuring live music, team challenges, and a space to connect with the entire Youth Tribe community.",
    features: ["Live local band performances", "Interactive team competitions", "Open mic talent showcases", "Community building activities"],
    href: "/OurRhythmPage"
  }, {
    number: "02",
    title: "Small Groups",
    description: "Focused, intimate sessions designed for meaningful conversation, personal growth, and building lasting friendships within the tribe.",
    features: ["Guided mentorship circles", "Peer-led discussion topics", "Safe space for reflection", "Collaborative goal setting"],
    href: "/OurRhythmPage"
  }, {
    number: "03",
    title: "Volunteer Projects",
    description: "Hands-on service opportunities that empower our youth to make a tangible impact in the local community and beyond.",
    features: ["Local park revitalization", "Community food bank support", "Youth-led charity drives", "Environmental cleanup days"],
    href: "/OurRhythmPage"
  }];
  return <section data-section-id="3998" id="what-we-do" className="bg-background text-foreground relative overflow-hidden py-20 md:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`text-center mb-16 md:mb-20 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-12 bg-border" />
            <span className="font-mono text-xs tracking-[0.3em] text-primary uppercase">PILLARS</span>
            <div className="h-px w-12 bg-border" />
          </div>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight tracking-tighter">
            Three ways to grow together.
          </h2>
        </div>

        {}
        <div ref={cardsRef} className={`grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-px bg-border border border-border rounded-lg overflow-hidden transition-all duration-700 ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {channels.map((channel, i) => <div key={i} data-index={i} className="group relative bg-card border border-border md:border-0 hover:border-primary transition-all duration-500 ease-out p-8 md:p-10 flex flex-col">
              {}
              <div className="mb-6">
                <span className="font-default text-6xl md:text-7xl font-bold text-primary leading-none block">
                  {channel.number}
                </span>
                <div className="mt-2 font-mono text-[10px] tracking-[0.3em] text-muted-foreground uppercase">
                  ACT / {channel.number}
                </div>
              </div>

              {}
              <h3 className="font-default text-2xl md:text-3xl font-bold text-foreground mb-6 leading-tight">
                {channel.title}
              </h3>

              {}
              <div className="mb-6 flex items-center gap-3">
                <div className="relative w-[50px] h-[50px] rounded-lg border border-border bg-secondary/60 flex items-center justify-center overflow-hidden">
                  {i === 0 && <Music className="size-6 text-primary relative z-10" />}
                  {i === 1 && <Users2 className="size-6 text-primary relative z-10" />}
                  {i === 2 && <Heart className="size-6 text-primary relative z-10" />}
                </div>
                <div className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase">
                  Status / Active
                </div>
              </div>

              {}
              <p className="font-default text-sm text-muted-foreground leading-relaxed mb-6">
                {channel.description}
              </p>

              {}
              <ul className="space-y-3 mb-8">
                {channel.features.map((feature, fi) => <li data-index={fi} key={fi} className="flex items-start gap-3 font-default text-sm text-foreground/90">
                    <Check className="size-4 text-primary mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <span>{feature}</span>
                  </li>)}
              </ul>

              {}
              <div className="mt-auto pt-6 border-t border-border">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col justify-between font-mono text-[10px] text-muted-foreground h-24 py-1">
                    <span>MAX</span>
                    <span>MID</span>
                    <span>MIN</span>
                  </div>
                  <div className="relative w-1 h-24 bg-secondary rounded-lg border border-border">
                    <div className="absolute left-1/2 -translate-x-1/2 w-6 h-3 rounded-sm bg-primary border border-primary-foreground/20 transition-all duration-500 ease-out shadow-md" style={{
                  top: "65%"
                }} />
                    <div className="absolute left-1/2 -translate-x-1/2 top-0 w-px h-full bg-border" />
                  </div>
                  <div className="flex-1 font-mono text-[10px] tracking-widest text-muted-foreground uppercase">
                    Impact / Scale
                  </div>
                </div>

                {}
                <div className="mt-6">
                  <Button asChild variant="link" className="px-0 text-primary hover:text-primary/80 font-mono text-xs tracking-widest uppercase">
                    <Link to={channel.href}>Learn more &#9656;</Link>
                  </Button>
                </div>
              </div>
            </div>)}
        </div>

        {}
        <div ref={footerRef} className={`mt-16 flex items-center justify-center gap-4 md:gap-6 transition-all duration-700 ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="h-px flex-1 max-w-[180px] bg-border" />
          <span className="font-mono text-[11px] md:text-xs tracking-[0.25em] text-muted-foreground uppercase text-center">
            Join the movement &mdash; Register for our next event
          </span>
          <div className="h-px flex-1 max-w-[180px] bg-border" />
        </div>
      </div>
    </section>;
}